import type {
  ArticleBlock,
  ArticleDocument,
  ArticleInlineNode,
  ArticleMedia,
  ArticleTextMark,
} from "./types";

export type EditorMark = { type: string; attrs?: Record<string, unknown> };

export type EditorNode = {
  type?: string;
  attrs?: Record<string, unknown>;
  marks?: EditorMark[];
  text?: string;
  content?: EditorNode[];
};

function previewUrl(asset: ArticleMedia | undefined) {
  if (!asset) return "";
  if (asset.src.startsWith("/media/cms/")) return `/api/media/${asset.id}/file`;
  const filename = asset.src.split("/").at(-1);
  return filename ? `/api/media/bootstrap/${encodeURIComponent(filename)}` : asset.src;
}

export function mediaPreviewUrl(asset: ArticleMedia) {
  return previewUrl(asset);
}

function articleMarksToEditor(marks: ArticleTextMark[] = []): EditorMark[] {
  return marks.map((mark) => ({ type: mark }));
}

function textToEditor(text: string, marks: EditorMark[]): EditorNode[] {
  const parts = text.split("\n");
  return parts.flatMap((part, index) => [
    ...(part ? [{ type: "text", text: part, marks: marks.length ? marks : undefined }] : []),
    ...(index < parts.length - 1 ? [{ type: "hardBreak" }] : []),
  ]);
}

function inlineToEditor(content: ArticleInlineNode[]): EditorNode[] {
  return content.flatMap((node) => {
    if (node.type === "text") return textToEditor(node.text, articleMarksToEditor(node.marks));
    return node.content.flatMap((child) => textToEditor(child.text, [
      ...articleMarksToEditor(child.marks),
      { type: "link", attrs: { href: node.href } },
    ]));
  });
}

function blockToEditor(block: ArticleBlock, media: Map<string, ArticleMedia>): EditorNode {
  if (block.type === "paragraph") return { type: "paragraph", content: inlineToEditor(block.content) };
  if (block.type === "heading") return {
    type: "heading",
    attrs: { level: block.level },
    content: inlineToEditor(block.content),
  };
  if (block.type === "list") return {
    type: block.style === "ordered" ? "orderedList" : "bulletList",
    content: block.items.map((item) => ({
      type: "listItem",
      content: [{ type: "paragraph", content: inlineToEditor(item) }],
    })),
  };
  if (block.type === "blockquote") return {
    type: "blockquote",
    attrs: { cite: block.cite ?? null },
    content: [{ type: "paragraph", content: inlineToEditor(block.content) }],
  };
  if (block.type === "note") return {
    type: "articleNote",
    attrs: { title: block.title ?? null },
    content: inlineToEditor(block.content),
  };
  if (block.type === "video") {
    const asset = media.get(block.mediaId);
    const poster = block.posterMediaId ? media.get(block.posterMediaId) : undefined;
    return {
      type: "articleVideo",
      attrs: {
        mediaId: block.mediaId,
        src: previewUrl(asset),
        posterMediaId: block.posterMediaId ?? null,
        poster: previewUrl(poster),
        caption: block.caption ?? null,
        autoplay: block.autoplay ?? false,
        loop: block.loop ?? false,
        muted: block.muted ?? true,
        controls: block.controls ?? true,
      },
    };
  }
  if (block.type !== "image") {
    return { type: "articleUnsupported", attrs: { raw: block } };
  }
  const asset = media.get(block.mediaId);
  return {
    type: "articleImage",
    attrs: {
      mediaId: block.mediaId,
      src: previewUrl(asset),
      alt: block.alt,
      caption: block.caption ?? null,
    },
  };
}

export function articleDocumentToEditor(document: ArticleDocument, media: ArticleMedia[]): EditorNode {
  const mediaById = new Map(media.map((asset) => [asset.id, asset]));
  return {
    type: "doc",
    content: document.blocks.map((block) => blockToEditor(block, mediaById)),
  };
}

function editorMarksToArticle(marks: EditorMark[] = []): ArticleTextMark[] {
  const supported = new Set<ArticleTextMark>(["bold", "italic", "underline"]);
  return marks.flatMap((mark) => supported.has(mark.type as ArticleTextMark)
    ? [mark.type as ArticleTextMark]
    : []);
}

function inlineFromEditor(nodes: EditorNode[] = []): ArticleInlineNode[] {
  const output: ArticleInlineNode[] = [];
  for (const node of nodes) {
    if (node.type === "hardBreak") {
      const previous = output.at(-1);
      if (previous?.type === "text") previous.text += "\n";
      else output.push({ type: "text", text: "\n" });
      continue;
    }
    if (node.type !== "text" || !node.text) continue;
    const link = node.marks?.find((mark) => mark.type === "link");
    const text = {
      type: "text" as const,
      text: node.text,
      marks: editorMarksToArticle(node.marks),
    };
    if (!text.marks.length) delete (text as { marks?: ArticleTextMark[] }).marks;
    if (link) {
      const href = String(link.attrs?.href ?? "");
      const previous = output.at(-1);
      if (previous?.type === "link" && previous.href === href) previous.content.push(text);
      else output.push({ type: "link", href, content: [text] });
    } else {
      output.push(text);
    }
  }
  return output;
}

function inlineFromBlocks(nodes: EditorNode[] = []): ArticleInlineNode[] {
  const output: ArticleInlineNode[] = [];
  for (const node of nodes) {
    if (["paragraph", "heading", "articleNote"].includes(node.type ?? "")) {
      if (output.length) output.push({ type: "text", text: "\n" });
      output.push(...inlineFromEditor(node.content));
    }
  }
  return output;
}

function blockFromEditor(node: EditorNode): ArticleBlock | null {
  if (node.type === "paragraph") return { type: "paragraph", content: inlineFromEditor(node.content) };
  if (node.type === "heading") return {
    type: "heading",
    level: node.attrs?.level === 3 ? 3 : 2,
    content: inlineFromEditor(node.content),
  };
  if (node.type === "bulletList" || node.type === "orderedList") return {
    type: "list",
    style: node.type === "orderedList" ? "ordered" : "unordered",
    items: (node.content ?? []).map((item) => inlineFromBlocks(item.content)),
  };
  if (node.type === "blockquote") return {
    type: "blockquote",
    content: inlineFromBlocks(node.content),
    ...(node.attrs?.cite ? { cite: String(node.attrs.cite) } : {}),
  };
  if (node.type === "articleNote") return {
    type: "note",
    content: inlineFromEditor(node.content),
    ...(node.attrs?.title ? { title: String(node.attrs.title) } : {}),
  };
  if (node.type === "articleImage") return {
    type: "image",
    mediaId: String(node.attrs?.mediaId ?? ""),
    alt: String(node.attrs?.alt ?? ""),
    ...(node.attrs?.caption ? { caption: String(node.attrs.caption) } : {}),
  };
  if (node.type === "articleVideo") return {
    type: "video",
    mediaId: String(node.attrs?.mediaId ?? ""),
    ...(node.attrs?.posterMediaId ? { posterMediaId: String(node.attrs.posterMediaId) } : {}),
    ...(node.attrs?.caption ? { caption: String(node.attrs.caption) } : {}),
    ...(node.attrs?.autoplay ? { autoplay: true } : {}),
    ...(node.attrs?.loop ? { loop: true } : {}),
    ...(node.attrs?.muted === false ? { muted: false } : {}),
    ...(node.attrs?.controls === false ? { controls: false } : {}),
  };
  if (node.type === "articleUnsupported" && node.attrs?.raw && typeof node.attrs.raw === "object") {
    return node.attrs.raw as ArticleBlock;
  }
  return null;
}

export function editorToArticleDocument(document: EditorNode): ArticleDocument {
  const blocks = (document.content ?? []).flatMap((node) => {
    const block = blockFromEditor(node);
    // Tiptap appends an empty editing paragraph after notes/media. It is not
    // content and ArticleDocument v1 deliberately requires nonempty paragraphs.
    if (block?.type === 'paragraph' && block.content.length === 0) return [];
    return block ? [block] : [];
  });
  return { version: 1, blocks: blocks.length ? blocks : [{ type: "paragraph", content: [] }] };
}
